/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "AI Decisioning permissions", description = "Control who can view, edit, and manage agents, messages, goals, and configuration in AI Decisioning using workspace roles.", lastUpdatedDate = new Date(1788220800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "AI Decisioning permissions",
        description: "Control who can view, edit, and manage agents, messages, goals, and configuration in AI Decisioning using workspace roles.",
        lastUpdatedDate: "Tue Sep 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Managing access",
          "depth": 0,
          "id": "managing-access"
        }, {
          "value": "Agent access by parent model",
          "depth": 0,
          "id": "agent-access-by-parent-model"
        }, {
          "value": "Permission details",
          "depth": 0,
          "id": "permission-details"
        }, {
          "value": "Agents",
          "depth": 1,
          "id": "agents"
        }, {
          "value": "Messages",
          "depth": 1,
          "id": "messages"
        }, {
          "value": "Tags",
          "depth": 1,
          "id": "tags"
        }, {
          "value": "Collections",
          "depth": 1,
          "id": "collections"
        }, {
          "value": "Goals",
          "depth": 1,
          "id": "goals"
        }, {
          "value": "Feature matrices",
          "depth": 1,
          "id": "feature-matrices"
        }, {
          "value": "Configuration",
          "depth": 1,
          "id": "configuration"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      ol: "ol",
      img: "img",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Workspace admins and team leads who manage access to AI Decisioning."
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["AI Decisioning permissions are managed through ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "roles"
        }), ", the same system that controls access to sources, destinations, and other Hightouch products. A role determines what each team member can modify in AI Decisioning, and which agents they can work with."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Every workspace member can ", _jsx(_components.strong, {
          children: "view"
        }), " AI Decisioning resources—agents, messages, goals, collections, and configuration. Editing requires one of two capabilities, granted per role on the ", _jsx(_components.strong, {
          children: "AI Decisioning"
        }), " tab of the role editor:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Capability"
            }), _jsx(_components.th, {
              children: "What it allows"
            }), _jsx(_components.th, {
              children: "Best for"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Edit content"
              })
            }), _jsx(_components.td, {
              children: "Edit message content, variables and variants, tags, collections, and content suggestions, and send preview messages."
            }), _jsx(_components.td, {
              children: "Marketing and content teams managing message copy, variants, and collections."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Manage agents"
              })
            }), _jsx(_components.td, {
              children: "Everything in Edit content, plus adding messages, agent creation and lifecycle, goals, feature matrices, channels, and configuration."
            }), _jsx(_components.td, {
              children: "Team leads responsible for agent setup, goals, channels, and configuration."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["In addition to these capabilities, a role controls ", _jsx(_components.strong, {
          children: "which agents"
        }), " the group can work with, based on the parent model each agent targets. See ", _jsx(_components.a, {
          href: "#agent-access-by-parent-model",
          children: "Agent access by parent model"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "A few rules apply regardless of role configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace admins"
          }), " always have full AI Decisioning access, on all parent models."]
        }), "\n", _jsxs(_components.li, {
          children: ["The other pre-built roles (Editor, Draft Editor, Viewer) don't include AI Decisioning editing. To give a non-admin group edit access, use a ", _jsx(_components.a, {
            href: "/workspace-management/roles#custom-roles",
            children: "custom role"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Every workspace member can see all AI Decisioning resources."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "managing-access",
        children: "Managing access"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Settings > Groups"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Edit ", _jsx(_components.a, {
            href: "/workspace-management/roles#custom-roles",
            children: "custom role"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "AI Decisioning"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Users can"
          }), ", select ", _jsx(_components.strong, {
            children: "Edit content"
          }), ", ", _jsx(_components.strong, {
            children: "Manage agents"
          }), ", or both. Manage agents includes everything Edit content allows."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Agent access by parent model"
          }), ", choose whether the role applies to ", _jsx(_components.strong, {
            children: "all parent models"
          }), " or only ", _jsx(_components.strong, {
            children: "specific parent models"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/permissions/role-editor-ai-decisioning-tab.png",
          alt: "The AI Decisioning tab of the role editor, showing the Edit content and Manage agents capabilities and agent access by parent model"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "agent-access-by-parent-model",
        children: "Agent access by parent model"
      }), "\n", _jsxs(_components.p, {
        children: ["Every agent targets a ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "parent model"
        }), "—the population of users it makes decisions for. Roles can limit which parent models a group works with:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All parent models"
          }), " — The role's capabilities apply to every agent in the workspace, including agents created later on new parent models."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Specific parent models"
          }), " — The role's capabilities apply only to agents targeting the selected parent models. Agents on other parent models remain visible but read-only."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/permissions/agent-access-specific-parent-models.png",
          alt: "Agent access by parent model with specific parent models selected"
        })
      }), "\n", _jsx(_components.p, {
        children: "Parent model access scopes everything tied to an agent: creating agents on that parent model, starting and pausing them, and editing their messages, goals, and experiments. Feature matrices are also scoped to the parent model they're built on."
      }), "\n", _jsx(_components.p, {
        children: "Workspace-level resources—channels, send events, and collections—aren't tied to a parent model, so they only require the corresponding capability."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For example, an \"Email content team\" role with ", _jsx(_components.strong, {
            children: "Edit content"
          }), " and access to the ", _jsx(_components.code, {
            children: "Customers"
          }), " parent model can edit message copy and variants on agents targeting ", _jsx(_components.code, {
            children: "Customers"
          }), ", but can't start or pause those agents, and can't edit messages on agents targeting other parent models."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "permission-details",
        children: "Permission details"
      }), "\n", _jsxs(_components.p, {
        children: ["The tables below show what each action requires. Actions on agents and their resources also require ", _jsx(_components.a, {
          href: "#agent-access-by-parent-model",
          children: "access to the agent's parent model"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "agents",
        children: "Agents"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Requires"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View agents"
            }), _jsx(_components.td, {
              children: "Any workspace member"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create agent"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Edit agent name"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Run agent or run all agents"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Start, pause, or initialize agent"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Edit agent configuration"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Delete agent"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "messages",
        children: "Messages"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Requires"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View messages"
            }), _jsx(_components.td, {
              children: "Any workspace member"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Add messages (individual or CSV upload)"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Edit message content (variables, variants)"
            }), _jsx(_components.td, {
              children: "Edit content"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Edit message name and configuration"
            }), _jsx(_components.td, {
              children: "Edit content"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Apply content suggestions"
            }), _jsx(_components.td, {
              children: "Edit content"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Send a preview or test message"
            }), _jsx(_components.td, {
              children: "Edit content"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Bulk edit message content"
            }), _jsx(_components.td, {
              children: "Edit content"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Delete or restore messages"
            }), _jsx(_components.td, {
              children: "Edit content"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Enable or disable messages (individual or bulk)"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "tags",
        children: "Tags"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Requires"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View tags"
            }), _jsx(_components.td, {
              children: "Any workspace member"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Edit message and variant tags"
            }), _jsx(_components.td, {
              children: "Edit content"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Generate tags"
            }), _jsx(_components.td, {
              children: "Edit content"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "collections",
        children: "Collections"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Requires"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View collections"
            }), _jsx(_components.td, {
              children: "Any workspace member"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create, edit, or delete collections"
            }), _jsx(_components.td, {
              children: "Edit content"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "goals",
        children: "Goals"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Requires"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View goals"
            }), _jsx(_components.td, {
              children: "Any workspace member"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Add, edit, or delete goals"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "feature-matrices",
        children: "Feature matrices"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Requires"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View feature matrices"
            }), _jsx(_components.td, {
              children: "Any workspace member"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create, edit, duplicate, or delete feature matrices"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "configuration",
        children: "Configuration"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Requires"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View configuration"
            }), _jsx(_components.td, {
              children: "Any workspace member"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Edit general, scheduling, and localization configuration"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Edit user features configuration"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create, edit, or delete channels"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Toggle channel enabled or disabled"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Edit send event configuration"
            }), _jsx(_components.td, {
              children: "Manage agents"
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The AI Decisioning REST API currently requires the workspace admin role for write operations."
        })
      })]
    });
  }
}
export default MDXContent;
